/**
 * IoT Onboard Chat - Conversational agentic device onboarding
 *
 * Modeled on ApiIngestChat: natural language input, phase-driven UI,
 * PDF drag-and-drop, session management.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cpFetch } from '../../lib/cpClient';
import { sendIoTChatMessage, loadIoTChatSession, loadIoTChatSessionByModel } from './api';
import WorkflowProgress from './components/WorkflowProgress';
import DeviceTypeCard from './components/DeviceTypeCard';
import type { IoTChatResponse, DiscoveredDeviceType } from './types';
import { Cpu, Send, Loader2, Upload, RotateCcw } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  ts: Date;
  discovered?: DiscoveredDeviceType | null;
  stepsCompleted?: string[];
  stepsRemaining?: string[];
  phase?: string;
  nextAction?: string;
}

interface IoTOnboardChatProps {
  sessionId: string | null;
  onSessionChange: (sid: string) => void;
  onDeviceReady?: (deviceId: string) => void;
  resumeModelId?: string;
}

type Phase = 'idle' | 'discovering' | 'awaiting_confirmation' | 'configuring' | 'provisioning' | 'active' | 'error';
const PROCESSING_PHASES: Phase[] = ['discovering', 'configuring', 'provisioning'];
const MAX_POLLS = 90;
const POLL_INTERVAL_MS = 3000;

export default function IoTOnboardChat({ sessionId, onSessionChange, onDeviceReady, resumeModelId }: IoTOnboardChatProps) {
  const initialAssistantMessage: Message = {
    role: 'assistant',
    content: 'Hi! Tell me the make and model of your IoT device (e.g., "Teltonika FMT100 GPS tracker" or "Dragino LHT65 temperature sensor") and I will figure out the rest. You can also drag and drop a PDF datasheet.',
    ts: new Date(),
  };
  const [messages, setMessages] = useState<Message[]>([
    initialAssistantMessage,
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>('idle');
  const [discovered, setDiscovered] = useState<DiscoveredDeviceType | null>(null);
  const [configured, setConfigured] = useState<any>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [bulkImeis, setBulkImeis] = useState('');
  const [registrationNotice, setRegistrationNotice] = useState<string | null>(null);
  const retryPhaseRef = useRef<'discovering' | 'configuring' | 'provisioning'>('discovering');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const resetChat = useCallback(() => {
    setMessages([{ ...initialAssistantMessage, ts: new Date() }]);
    setInput('');
    setIsProcessing(false);
    setCurrentPhase('idle');
    setDiscovered(null);
    setConfigured(null);
    setBulkImeis('');
    setRegistrationNotice(null);
    retryPhaseRef.current = 'discovering';
    onSessionChange('');
  }, [initialAssistantMessage, onSessionChange]);

  function shouldPollPhase(phase?: string): boolean {
    return PROCESSING_PHASES.includes((phase || 'idle') as Phase);
  }

  async function startPolling(pollId: string) {
    let polls = 0;
    while (polls < MAX_POLLS) {
      polls++;
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const pollResp = await loadIoTChatSession(pollId);
        const s = pollResp?.session;
        if (!s || shouldPollPhase(s.phase)) continue;

        const lastAssistant = [...(s.messages || [])].reverse().find((m: any) => m.role === 'assistant');
        setMessages((prev) => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].content?.toLowerCase().includes('searching')) {
            updated.pop();
          }
          updated.push({
            role: 'assistant',
            content: lastAssistant?.content || s.discovered?.summary || 'Discovery complete.',
            ts: new Date(),
            discovered: s.discovered || null,
            phase: s.phase,
          });
          return updated;
        });
        setCurrentPhase(s.phase as Phase);
        if (s.discovered) setDiscovered(s.discovered);
        if (s.configured) setConfigured(s.configured);
        if (s.last_phase && ['discovering', 'configuring', 'provisioning'].includes(s.last_phase)) {
          retryPhaseRef.current = s.last_phase as 'discovering' | 'configuring' | 'provisioning';
        }
        setIsProcessing(false);
        return;
      } catch {
        // Continue polling until max attempts.
      }
    }
    setIsProcessing(false);
  }

  // Restore or reset chat when context changes
  useEffect(() => {
    if (sessionId) {
      restoreSession(sessionId);
    } else if (resumeModelId) {
      loadSessionByModelId(resumeModelId);
    } else {
      resetChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, resumeModelId]);

  async function restoreSession(sid: string) {
    try {
      const resp = await loadIoTChatSession(sid);
      if (!resp?.success || !resp.session) return;
      const s = resp.session;

      const restoredMsgs: Message[] = (s.messages || []).map((m: any) => ({
        role: m.role,
        content: m.content,
        ts: new Date(m.ts),
      }));

      // Enrich last assistant message with metadata
      if (s.discovered && restoredMsgs.length > 0) {
        const lastAssistant = [...restoredMsgs].reverse().find((m) => m.role === 'assistant');
        if (lastAssistant) {
          lastAssistant.discovered = s.discovered;
          lastAssistant.phase = s.phase;
        }
      }

      setMessages(restoredMsgs);
      setCurrentPhase(s.phase as Phase);
      setDiscovered(s.discovered || null);
      setConfigured(s.configured || null);
      if (s.last_phase && ['discovering', 'configuring', 'provisioning'].includes(s.last_phase)) {
        retryPhaseRef.current = s.last_phase;
      } else if (shouldPollPhase(s.phase)) {
        retryPhaseRef.current = s.phase as 'discovering' | 'configuring' | 'provisioning';
      }
    } catch {
      // Ignore restore errors
    }
  }

  async function loadSessionByModelId(modelId: string) {
    try {
      const resp = await loadIoTChatSessionByModel(modelId);
      if (!resp?.success || !resp.session?.session_id) return;
      onSessionChange(resp.session.session_id);
      await restoreSession(resp.session.session_id);
    } catch {
      // Ignore missing session for model.
    }
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || isProcessing) return;

    const userMsg: Message = { role: 'user', content: text, ts: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsProcessing(true);

    if (currentPhase === 'idle') setCurrentPhase('discovering');

    let startedPolling = false;
    try {
      const resp: IoTChatResponse = await sendIoTChatMessage({
        session_id: sessionId || undefined,
        input: text,
      });

      if (resp.session_id && resp.session_id !== sessionId) {
        onSessionChange(resp.session_id);
      }

      // If backend says poll, start polling for the async result
      if (resp.next_action === 'poll' && resp.session_id) {
        startedPolling = true;
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: resp.response || 'Searching...', ts: new Date(), stepsCompleted: resp.steps_completed, stepsRemaining: resp.steps_remaining },
        ]);
        await startPolling(resp.session_id);
        return;
      }

      handleResponse(resp);
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${e?.message || 'Unknown error'}`, ts: new Date() },
      ]);
      setCurrentPhase('error');
    } finally {
      if (!startedPolling) setIsProcessing(false);
    }
  }

  function handleResponse(resp: IoTChatResponse) {
    const phase = (resp.phase || 'idle') as Phase;
    setCurrentPhase(phase);
    if (PROCESSING_PHASES.includes(phase)) {
      retryPhaseRef.current = phase as 'discovering' | 'configuring' | 'provisioning';
    } else if (phase === 'error' && resp.last_phase && ['discovering', 'configuring', 'provisioning'].includes(resp.last_phase)) {
      retryPhaseRef.current = resp.last_phase as 'discovering' | 'configuring' | 'provisioning';
    }

    if (resp.discovered) setDiscovered(resp.discovered);
    if (resp.configured) setConfigured(resp.configured);

    const assistantMsg: Message = {
      role: 'assistant',
      content: resp.response || 'Done.',
      ts: new Date(),
      discovered: resp.discovered || null,
      stepsCompleted: resp.steps_completed,
      stepsRemaining: resp.steps_remaining,
      phase: resp.phase,
      nextAction: resp.next_action,
    };

    setMessages((prev) => [...prev, assistantMsg]);

    if (resp.configured?.devices_registered?.[0]) {
      onDeviceReady?.(resp.configured.devices_registered[0]);
    }
  }

  async function handleConfirmation(confirmed: boolean) {
    if (isProcessing) return;
    setIsProcessing(true);

    if (confirmed) {
      setCurrentPhase('configuring');
    }

    let startedPolling = false;
    try {
      const userContent = confirmed ? 'Confirmed - proceed with setup' : 'No, try again with different approach';
      setMessages((prev) => [...prev, { role: 'user', content: userContent, ts: new Date() }]);

      if (!confirmed) {
        // Start new discovery
        setCurrentPhase('idle');
        setDiscovered(null);
        setIsProcessing(false);
        return;
      }

      const resp: IoTChatResponse = await sendIoTChatMessage({
        session_id: sessionId || undefined,
        input: '',
        confirmation: true,
      });

      if (resp.next_action === 'poll' && resp.session_id) {
        startedPolling = true;
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: resp.response || 'Configuring device type...', ts: new Date(), stepsCompleted: resp.steps_completed, stepsRemaining: resp.steps_remaining },
        ]);
        await startPolling(resp.session_id);
        return;
      }

      handleResponse(resp);
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${e?.message || 'Unknown error'}`, ts: new Date() },
      ]);
      setCurrentPhase('error');
    } finally {
      if (!startedPolling) setIsProcessing(false);
    }
  }

  async function handleBulkRegister() {
    const ids = bulkImeis
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length === 0 || isProcessing) return;

    setIsProcessing(true);
    setMessages((prev) => [...prev, { role: 'user', content: `Register ${ids.length} device(s): ${ids.join(', ')}`, ts: new Date() }]);

    try {
      const resp: IoTChatResponse = await sendIoTChatMessage({
        session_id: sessionId || undefined,
        input: '',
        device_ids: ids,
      });
      handleResponse(resp);
      if (resp.response?.toLowerCase().includes('registered') && !resp.error) {
        setRegistrationNotice(resp.response);
      } else {
        setRegistrationNotice(null);
      }
      setBulkImeis('');
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error registering devices: ${e?.message || 'Unknown error'}`, ts: new Date() },
      ]);
      setRegistrationNotice(null);
    } finally {
      setIsProcessing(false);
    }
  }

  // PDF drag and drop
  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }
  function handleDragLeave() {
    setIsDragging(false);
  }
  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (file.type === 'application/pdf') {
      setIsProcessing(true);
      let startedPolling = false;
      try {
        // Upload to DAM
        const formData = new FormData();
        formData.append('file', file);
        formData.append('compress', 'true');
        const uploadResp: any = await cpFetch('/dam/assets/upload', { method: 'POST', body: formData });
        if (!uploadResp?.success || !uploadResp?.asset_id) throw new Error('DAM upload failed');

        // Extract text
        const extractResp: any = await cpFetch(`/dam/assets/${encodeURIComponent(uploadResp.asset_id)}/extract-text`, {
          method: 'POST',
          body: JSON.stringify({ force: false }),
        });
        const text = extractResp?.extracted_text || '';

        setMessages((prev) => [
          ...prev,
          { role: 'user', content: `[PDF uploaded: ${file.name}]`, ts: new Date() },
        ]);

        // Send as attachment
        const resp: IoTChatResponse = await sendIoTChatMessage({
          session_id: sessionId || undefined,
          input: `Datasheet from ${file.name}`,
          attachments: [{ type: 'pdf', dam_asset_id: uploadResp.asset_id, content: text }],
        });

        if (resp.session_id && resp.session_id !== sessionId) {
          onSessionChange(resp.session_id);
        }

        if (resp.next_action === 'poll' && resp.session_id) {
          startedPolling = true;
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: resp.response || 'Searching for device specifications...', ts: new Date(), stepsCompleted: resp.steps_completed, stepsRemaining: resp.steps_remaining },
          ]);
          await startPolling(resp.session_id);
          return;
        }
        handleResponse(resp);
      } catch (e: any) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `PDF processing failed: ${e?.message}`, ts: new Date() },
        ]);
      } finally {
        if (!startedPolling) setIsProcessing(false);
      }
    }
  }

  async function handleRetry() {
    if (isProcessing || !sessionId) return;
    setIsProcessing(true);
    try {
      const resp: IoTChatResponse = await sendIoTChatMessage({
        session_id: sessionId,
        input: '',
        retry_phase: retryPhaseRef.current,
      });
      if (resp.next_action === 'poll' && resp.session_id) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: resp.response || 'Retrying failed phase...', ts: new Date(), stepsCompleted: resp.steps_completed, stepsRemaining: resp.steps_remaining },
        ]);
        await startPolling(resp.session_id);
      } else {
        handleResponse(resp);
      }
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Retry failed: ${e?.message}`, ts: new Date() },
      ]);
    } finally {
      setIsProcessing(false);
    }
  }

  const phaseLabel: Record<Phase, string> = {
    idle: 'Ready',
    discovering: 'Discovering',
    awaiting_confirmation: 'Review Config',
    configuring: 'Configuring',
    provisioning: 'Provisioning',
    active: 'Active',
    error: 'Error',
  };

  const phaseColor: Record<Phase, string> = {
    idle: 'bg-gray-100 text-gray-600',
    discovering: 'bg-blue-100 text-blue-700',
    awaiting_confirmation: 'bg-amber-100 text-amber-700',
    configuring: 'bg-purple-100 text-purple-700',
    provisioning: 'bg-indigo-100 text-indigo-700',
    active: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
  };

  return (
    <div
      className={`flex flex-col h-full bg-white rounded-lg border ${isDragging ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200'} shadow-sm`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-cyan-600 rounded-lg">
            <Cpu className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">IoT Device Onboarding</h3>
            <p className="text-xs text-gray-500">AI-powered device setup</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 1 && (
            <button
              onClick={resetChat}
              className="px-2.5 py-1 text-xs font-medium border border-gray-300 rounded-full hover:bg-white transition-colors"
              disabled={isProcessing}
            >
              New Session
            </button>
          )}
          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${phaseColor[currentPhase]}`}>
            {phaseLabel[currentPhase]}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg p-3 ${
              msg.role === 'user'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-900'
            }`}>
              <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
              <div className="text-xs mt-1.5 opacity-60">
                {msg.ts.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}

        {/* Device Type Card (awaiting confirmation) */}
        {currentPhase === 'awaiting_confirmation' && discovered && (
          <DeviceTypeCard
            discovered={discovered}
            onConfirm={() => handleConfirmation(true)}
            onReject={() => handleConfirmation(false)}
            isProcessing={isProcessing}
          />
        )}

        {/* Workflow progress */}
        {isProcessing && messages.length > 0 && (
          <div className="max-w-[85%]">
            <WorkflowProgress
              stepsCompleted={messages[messages.length - 1]?.stepsCompleted || []}
              stepsRemaining={messages[messages.length - 1]?.stepsRemaining || []}
              isProcessing
            />
          </div>
        )}

        {/* Bulk IMEI registration (active phase) */}
        {currentPhase === 'active' && (
          <div className="rounded-lg border border-green-200 bg-green-50/50 p-4 space-y-3">
            {registrationNotice && (
              <div className="rounded-md border border-emerald-200 bg-emerald-100 px-3 py-2 text-xs text-emerald-900">
                {registrationNotice}
              </div>
            )}
            <div className="text-sm font-semibold text-gray-900">Register Device Instances</div>
            <p className="text-xs text-gray-500">
              Paste IMEIs or serial numbers (one per line, or comma-separated) to register device instances for this model.
            </p>
            <textarea
              value={bulkImeis}
              onChange={(e) => setBulkImeis(e.target.value)}
              placeholder="350612071541138&#10;350612071541139&#10;350612071541140"
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isProcessing}
            />
            <button
              onClick={handleBulkRegister}
              disabled={isProcessing || !bulkImeis.trim()}
              className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 transition-colors"
            >
              Register Devices
            </button>
          </div>
        )}

        {/* Error retry button */}
        {currentPhase === 'error' && (
          <button
            onClick={handleRetry}
            disabled={isProcessing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Retry
          </button>
        )}

        {/* Processing indicator */}
        {isProcessing && (
          <div className="flex justify-start">
            <div className="rounded-lg p-3 bg-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>
                  {currentPhase === 'discovering' && 'Searching for device specifications...'}
                  {currentPhase === 'configuring' && 'Generating template and decoder...'}
                  {currentPhase === 'provisioning' && 'Creating device type artifacts...'}
                  {!['discovering', 'configuring', 'provisioning'].includes(currentPhase) && 'Processing...'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-50/80 border-2 border-dashed border-blue-400 rounded-lg z-10">
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-blue-500" />
            <span className="text-sm font-medium text-blue-700">Drop PDF datasheet here</span>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={
              currentPhase === 'idle'
                ? 'Describe your device (e.g., "Teltonika FMT100 GPS tracker")...'
                : currentPhase === 'awaiting_confirmation'
                  ? 'Ask a question or confirm above...'
                  : 'Type a message...'
            }
            className="flex-1 min-h-[44px] max-h-[120px] px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
            disabled={isProcessing}
            rows={1}
          />
          <button
            onClick={handleSend}
            disabled={isProcessing || !input.trim()}
            className="p-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
